import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { prefetchStaffDemand, STAFF_DEMAND_CACHE_KEY } from '../utils/prefetchStaffDemand';
import { clearPageCache } from '../utils/pageCache';

const TenantContext = createContext();

export const TenantProvider = ({ children }) => {
    const [currentTenant, setCurrentTenant] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [sites, setSites] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingSites, setIsLoadingSites] = useState(false);
    const previousTokenRef = useRef(localStorage.getItem('token'));

    // Decode JWT to get tenant info
    const decodeToken = (token) => {
        try {
            // Get the payload part (middle section of JWT)
            const parts = token.split('.');
            if (parts.length !== 3) {
                console.error('Invalid token format');
                return null;
            }

            let payload = parts[1];

            // Handle URL-safe Base64 encoding (replace - with + and _ with /)
            // Also add padding if needed
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const paddedBase64 = base64 + '='.repeat((4 - base64.length % 4) % 4);

            const decoded = JSON.parse(atob(paddedBase64));
            return decoded;
        } catch (e) {
            console.error('Error decoding token:', e);
            return null;
        }
    };

    // Fetch tenants from API
    const fetchTenantsFromApi = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/my-tenants`);

            if (response.ok) {
                const tenantsData = await response.json();
                // Store in localStorage for offline access
                localStorage.setItem('tenants', JSON.stringify(tenantsData));
                return tenantsData;
            } else {
                console.error('Failed to fetch tenants from API:', response.status);
                return null;
            }
        } catch (error) {
            console.error('Error fetching tenants from API:', error);
            return null;
        }
    };

    // Initialize tenant - fetch from API with fallback to localStorage/token
    const initializeTenant = async () => {
        // First, try to get tenants from API (most up-to-date)
        const apiTenants = await fetchTenantsFromApi();

        if (apiTenants && apiTenants.length > 0) {
            setTenants(apiTenants);

            const token = localStorage.getItem('token');
            const decoded = token ? decodeToken(token) : null;
            const jwtTenantId = decoded?.tenantId || decoded?.tenant_id;
            const jwtMatch = jwtTenantId
                ? apiTenants.find((tenant) => String(tenant.id) === String(jwtTenantId))
                : null;

            if (jwtMatch) {
                const selected = { id: jwtMatch.id, name: jwtMatch.name };
                setCurrentTenant(selected);
                localStorage.setItem('currentTenant', JSON.stringify(selected));
            } else {
                // Check for previously saved selected tenant
                const storedCurrentTenant = localStorage.getItem('currentTenant');
                if (storedCurrentTenant) {
                    const parsed = JSON.parse(storedCurrentTenant);
                    // Verify the tenant still exists in the tenants array
                    const tenantExists = apiTenants.some(t => t.id === parsed.id);
                    if (tenantExists) {
                        setCurrentTenant(parsed);
                    } else {
                        // Tenant no longer exists, fall back to first tenant
                        setCurrentTenant({
                            id: apiTenants[0].id,
                            name: apiTenants[0].name
                        });
                    }
                } else {
                    // Only set to first tenant if no saved tenant (first login)
                    setCurrentTenant({
                        id: apiTenants[0].id,
                        name: apiTenants[0].name
                    });
                }
            }
            setIsLoading(false);
            return;
        }

        // Fallback 1: Try localStorage (set during login)
        const storedTenants = localStorage.getItem('tenants');
        const storedCurrentTenant = localStorage.getItem('currentTenant');

        if (storedTenants) {
            try {
                const tenantsArray = JSON.parse(storedTenants);
                setTenants(tenantsArray);

                if (storedCurrentTenant) {
                    const parsed = JSON.parse(storedCurrentTenant);
                    const tenantExists = tenantsArray.some(t => t.id === parsed.id);
                    if (tenantExists) {
                        setCurrentTenant(parsed);
                    } else if (tenantsArray.length > 0) {
                        setCurrentTenant({
                            id: tenantsArray[0].id,
                            name: tenantsArray[0].name
                        });
                    }
                } else if (tenantsArray.length > 0) {
                    setCurrentTenant({
                        id: tenantsArray[0].id,
                        name: tenantsArray[0].name
                    });
                }
            } catch (e) {
                console.error('Error parsing tenants from localStorage:', e);
            }
        } else {
            // Fallback 2: Try to decode from token
            const token = localStorage.getItem('token');
            if (token) {
                const decoded = decodeToken(token);
                if (decoded && decoded.tenants && Array.isArray(decoded.tenants)) {
                    setTenants(decoded.tenants);
                    if (decoded.tenants.length > 0) {
                        setCurrentTenant({
                            id: decoded.tenants[0].id,
                            name: decoded.tenants[0].name
                        });
                    }
                }
            }
        }
        setIsLoading(false);
    };

    // Fetch sites from API (optionally filtered by tenant)
    const fetchSites = useCallback(async (tenantId = null) => {
        setIsLoadingSites(true);

        try {
            let url = `${API_BASE_URL}/sites`;
            if (tenantId) {
                url += `?tenant_id=${tenantId}`;
            }

            const response = await fetchWithAuth(url);

            if (response.ok) {
                const sitesData = await response.json();
                // Transform snake_case to camelCase for frontend
                const transformedSites = sitesData.map(site => ({
                    siteId: site.site_id,
                    siteName: site.name,
                    tenantId: site.tenant_id,
                    tenantName: site.tenant_name,
                    status: site.status
                }));
                setSites(transformedSites);
                setIsLoadingSites(false);
                return transformedSites;
            } else {
                console.error('Failed to fetch sites:', response.status);
                setIsLoadingSites(false);
                return [];
            }
        } catch (error) {
            console.error('Error fetching sites:', error);
            setIsLoadingSites(false);
            return [];
        }
    }, []);

    // Fetch all tenants (not just the current user's tenants)
    const fetchAllTenants = useCallback(async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/tenants`);

            if (response.ok) {
                const tenantsData = await response.json();
                // Transform snake_case to camelCase for frontend
                const transformedTenants = tenantsData.map(t => ({
                    id: t.tenant_id,
                    name: t.tenant_name,
                    status: t.status
                }));
                return transformedTenants;
            } else {
                console.error('Failed to fetch tenants:', response.status);
                return [];
            }
        } catch (error) {
            console.error('Error fetching tenants:', error);
            return [];
        }
    }, []);

    // Initial load
    useEffect(() => {
        initializeTenant();
    }, []);

    // Watch for token changes (new login in same tab or cross-tab)
    useEffect(() => {
        const handleTokenChanged = () => {
            const currentToken = localStorage.getItem('token');
            if (currentToken && currentToken !== previousTokenRef.current) {
                previousTokenRef.current = currentToken;
                initializeTenant();
            }
        };
        // app:tokenChanged fires from Login.js in same tab; storage fires from other tabs
        window.addEventListener('app:tokenChanged', handleTokenChanged);
        window.addEventListener('storage', handleTokenChanged);
        return () => {
            window.removeEventListener('app:tokenChanged', handleTokenChanged);
            window.removeEventListener('storage', handleTokenChanged);
        };
    }, []);

    // Function to update tenant after switching
    const updateActiveTenant = (newTenant) => {
        if (currentTenant?.id && newTenant?.id && currentTenant.id !== newTenant.id) {
            clearPageCache(STAFF_DEMAND_CACHE_KEY(currentTenant.id));
        }
        setCurrentTenant(newTenant);
    };

    // Warm staff demand report into pageCache after tenant is ready (non-blocking)
    useEffect(() => {
        if (!currentTenant?.id || isLoading) return;
        const tenantId = currentTenant.id;
        const run = () => {
            prefetchStaffDemand(tenantId).catch(() => {});
        };
        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
            const idleId = window.requestIdleCallback(run, { timeout: 2500 });
            return () => window.cancelIdleCallback?.(idleId);
        }
        const t = setTimeout(run, 400);
        return () => clearTimeout(t);
    }, [currentTenant?.id, isLoading]);

    // Function to refresh tenants from API
    const refreshTenants = async () => {
        setIsLoading(true);
        try {
            const apiTenants = await fetchTenantsFromApi();
            if (apiTenants && apiTenants.length > 0) {
                setTenants(apiTenants);
                // Check if current tenant still exists
                if (currentTenant) {
                    const tenantExists = apiTenants.some(t => t.id === currentTenant.id);
                    if (!tenantExists) {
                        setCurrentTenant({
                            id: apiTenants[0].id,
                            name: apiTenants[0].name
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Error refreshing tenants:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Get sites for a specific tenant
    const getSitesForTenant = useCallback((tenantId) => {
        return sites.filter(site => site.tenantId === tenantId);
    }, [sites]);

    // Get site by ID
    const getSiteById = useCallback((siteId) => {
        return sites.find(site => site.siteId === siteId);
    }, [sites]);

    return (
        <TenantContext.Provider value={{
            currentTenant,
            setCurrentTenant: updateActiveTenant,
            tenants,
            setTenants,
            sites,
            setSites,
            isLoading,
            isLoadingSites,
            refreshTenants,
            fetchSites,
            fetchAllTenants,
            getSitesForTenant,
            getSiteById
        }}>
            {children}
        </TenantContext.Provider>
    );
};

export const useTenant = () => useContext(TenantContext);

export default TenantContext;
